import { useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import Navigation from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Mic2, ArrowRight } from "lucide-react";

interface SeoPageLayoutProps {
  children: React.ReactNode;
  title: string;
  titleAccent?: string;
  metaTitle?: string;
  metaDescription?: string;
  slug?: string;
  jsonLd?: object;
}

export const SeoPageLayout = ({ children, title, titleAccent, metaTitle, metaDescription, slug, jsonLd }: SeoPageLayoutProps) => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const fullTitle = metaTitle || `${title}${titleAccent ? ` ${titleAccent}` : ''} - WystepKomediowy.pl`;
  const description = metaDescription || `${title}${titleAccent ? ` ${titleAccent}` : ''} - profesjonalne występy komediowe dla firm. Zamów komika na swoje wydarzenie!`;
  const canonicalUrl = `https://wystepkomediowy.pl${slug || pathname}`;

  const localBusinessJsonLd = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "WystepKomediowy.pl",
    "url": "https://wystepkomediowy.pl",
    "email": "kontakt@wystepkomediowy.pl",
    "telephone": "+48533333993",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "ul. Księcia Witolda 49/15",
      "addressLocality": "Wrocław",
      "postalCode": "50-202",
      "addressCountry": "PL"
    },
    "description": description,
    "priceRange": "$$",
    "areaServed": {
      "@type": "Country",
      "name": "Polska"
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{fullTitle}</title>
        <meta name="description" content={description} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={fullTitle} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="WystepKomediowy.pl" />
        <script type="application/ld+json">{JSON.stringify(localBusinessJsonLd)}</script>
        {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
      </Helmet>
      <Navigation />

      {/* Hero Banner */}
      <section className="relative pt-28 pb-16 md:pt-36 md:pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary/95 to-brand-purple" />
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-72 h-72 bg-brand-magenta rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-brand-orange rounded-full blur-3xl" />
        </div>
        <div className="container max-w-4xl mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-white/10 backdrop-blur-sm">
                <Mic2 className="w-5 h-5 text-brand-orange" />
              </div>
              <span className="text-sm font-medium text-white/70 uppercase tracking-wider">Komediowa rozrywka dla firm</span>
            </div>
            <h1 className="text-3xl md:text-5xl font-montserrat font-bold text-white leading-tight">
              {title}{" "}
              {titleAccent && (
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-brand-orange via-brand-magenta to-brand-purple-light">
                  {titleAccent}
                </span>
              )}
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <main className="pb-20">
        <div className="container max-w-4xl mx-auto px-4 -mt-8 relative z-10">
          <div className="bg-white rounded-2xl shadow-xl border border-border/50 p-6 md:p-10 space-y-8">
            <div className="seo-content space-y-8">
              {children}
            </div>
          </div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mt-12 p-8 md:p-10 rounded-2xl bg-gradient-to-br from-primary via-primary/95 to-brand-purple text-center relative overflow-hidden"
          >
            <div className="absolute inset-0 opacity-10">
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-brand-orange rounded-full blur-2xl" />
              <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-brand-magenta rounded-full blur-2xl" />
            </div>
            <div className="relative z-10">
              <h2 className="text-2xl md:text-3xl font-montserrat font-bold text-white mb-4">
                Zainteresowany występem komediowym?
              </h2>
              <p className="text-white/80 mb-6 max-w-lg mx-auto">
                Skontaktuj się z nami i zamów niezapomniane widowisko dla Twojej firmy lub wydarzenia.
              </p>
              <Link
                to="/#kontakt"
                onClick={() => {
                  setTimeout(() => {
                    const element = document.getElementById('kontakt');
                    if (element) element.scrollIntoView({ behavior: 'smooth' });
                  }, 100);
                }}
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-white text-primary font-semibold hover:bg-white/90 transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
              >
                Zamów występ
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        </div>
      </main>
      <Footer />
    </div>
  );
};
